import React, { useRef, useState } from "react";
import {
  FiChevronDown, FiChevronUp, FiSearch, FiCalendar, FiPlusCircle, FiCheck,
} from "react-icons/fi";
import { MdOutlineFilterList, MdMic, MdSave } from "react-icons/md";
import { BsPlusCircleFill, BsTrashFill } from "react-icons/bs";
import { AiOutlineEdit } from "react-icons/ai";
import filterIcon from "../images/filter.png"
import ExaminationRenderer from "./Examination/ExaminationRenderer";
import DynamicExamination from "./Examination/DynamicExamination";
const useSpeechToText = () => {

  const startListening = (onResult: (text: string) => void) => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;

    if (!SpeechRecognition) {
      alert("Speech recognition not supported");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-IN";
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event: any) => {
      const transcript = event.results[0][0].transcript;
      // console.log("Heard:", transcript);
      onResult(transcript);
    };

  recognition.onerror = (event: any) => {
  if (event.error === "not-allowed") {
    alert("Microphone permission denied. Please allow mic access in browser.");
  } else {
    console.error("Speech error:", event.error);
  }
};

    recognition.start();
  };

  return { startListening };
};

// ─── Types ────────────────────────────────────────────────────────────────────
interface ComplaintRow {
  problem: string; severity: string; bodyPart: string; duration: string; unit: string;
}
interface DiagnosisRow {
  icdCode: string; diagnosisName: string; stage: string;
}
interface MedicineRow {
  medicine: string; dose: string; frequency: string;
  morning: string; afternoon: string; evening: string; night: string;
  days: string; startDate: string; route: string; mealRelation: string; instructions: string;
}
interface ServiceRow {
  id: number; serviceName: string; department: string; subDepartment: string; priority: string;
}

const SEVERITY_OPTIONS  = ["Low", "Medium", "High"];
const UNIT_OPTIONS      = ["Hr", "Min", "Days", "Month", "Years"];
const DOSE_OPTIONS      = ["OD","BD","TDS","QID","HS","SOS","STAT","PRN","Q4H","Q6H","Q8H","Q12H","QOD"];
const FREQUENCY_OPTIONS = ["0-0-0-1","0-0-1-1","0-1-0-1","1-0-0-1","1-1-0-0","1-1-1-1","1-1-1-0","1-0-1-0"];
const ROUTE_OPTIONS     = ["Oral","Oral (PO)","IV","IM","SC","SL","INH","PR","ID","PEG","Ophthalmic","Otic","Intranasal","NG"];
const MEAL_OPTIONS      = ["Before meal","After meal","With meal","Empty stomach"];
const PRIORITY_OPTIONS  = ["Routine","Urgent","STAT","As Soon As Possible"];

const EMPTY_MED_FORM = (): MedicineRow => ({
  medicine:"", dose:"", frequency:"", morning:"0", afternoon:"0",
  evening:"0", night:"0", days:"", startDate: new Date().toLocaleDateString("en-GB"),
  route:"Oral", mealRelation:"After meal", instructions:"",
});

// ─── Dummy Data ───────────────────────────────────────────────────────────────
const DUMMY_SERVICES = [
  { id:1,  serviceName:"Blood Grouping and RH Factor",          department:"Pathology",  subDepartment:"Hematology"         },
  { id:2,  serviceName:"Blood Sugar [Random], Fluoride Plasma", department:"Pathology",  subDepartment:"Biochemistry"       },
  { id:3,  serviceName:"Blood Sugar 2 Hr. PP, Fluoride Plasma", department:"Pathology",  subDepartment:"Biochemistry"       },
  { id:4,  serviceName:"Blood Sugar Fasting, Fluoride Plasma",  department:"Pathology",  subDepartment:"Biochemistry"       },
  { id:5,  serviceName:"Blood Sugar PP (After Lunch)",          department:"Pathology",  subDepartment:"Biochemistry"       },
  { id:6,  serviceName:"Complete Blood Count (CBC)",            department:"Pathology",  subDepartment:"Hematology"         },
  { id:7,  serviceName:"Lipid Profile",                         department:"Pathology",  subDepartment:"Biochemistry"       },
  { id:8,  serviceName:"Liver Function Test (LFT)",             department:"Pathology",  subDepartment:"Biochemistry"       },
  { id:9,  serviceName:"Urine Routine Examination",             department:"Pathology",  subDepartment:"Clinical Pathology" },
  { id:10, serviceName:"Chest X-Ray PA View",                   department:"Radiology",  subDepartment:"X-Ray"              },
  { id:11, serviceName:"ECG (Electrocardiogram)",               department:"Cardiology", subDepartment:"Diagnostics"        },
  { id:12, serviceName:"USG Abdomen & Pelvis",                  department:"Radiology",  subDepartment:"Ultrasound"         },
  { id:13, serviceName:"HbA1c (Glycated Haemoglobin)",          department:"Pathology",  subDepartment:"Biochemistry"       },
  { id:14, serviceName:"Thyroid Profile (T3, T4, TSH)",         department:"Pathology",  subDepartment:"Endocrinology"      },
  { id:15, serviceName:"MRI Brain Plain",                       department:"Radiology",  subDepartment:"MRI"                },
];

const DUMMY_MEDICINES = [
  "TAB D RISE-60K (1+1)", "TAB PARACETAMOL 500MG",
  "TAB METFORMIN 500MG",  "CAP AMOXICILLIN 500MG", "SYP AZITHROMYCIN 200MG",
];

const DUMMY_DIAGNOSES = [
  { id:"3B21.Y", title:"Haemorrhagic disorder due to other specified circulating anticoagulants or coagulation factors" },
  { id:"3B14.2", title:"Other inherited coagulation factor deficiency with bleeding tendency, unspecified" },
  { id:"3B81.2", title:"Atrophy of spleen" },
  { id:"5A00.ZZ",title:"Acquired hypothyroidism, unspecified" },
  { id:"BA80",   title:"Essential (primary) hypertension" },
  { id:"5A11",   title:"Type 2 diabetes mellitus" },
  { id:"CA22.Z", title:"Migraine, unspecified" },
];


type PlanTab = "investigation" | "medication" | "surgery" | "diagnosis" | "admission";

// ─── Validation Helpers ───────────────────────────────────────────────────────
const getMissingComplaintFields = (row: ComplaintRow): string[] => {
  const m: string[] = [];
  if (!row.problem.trim())  m.push("Problem");
  if (!row.severity)        m.push("Severity");
  if (!row.bodyPart.trim()) m.push("Body Part");
  if (!row.duration.trim()) m.push("Duration");
  return m;
};
const getMissingMedFields = (row: MedicineRow): string[] => {
  const m: string[] = [];
  if (!row.medicine.trim())  m.push("Medicine");
  if (!row.dose)             m.push("Dose");
  if (!row.frequency)        m.push("Frequency");
  if (!row.days.trim())      m.push("Days");
  if (!row.startDate.trim()) m.push("Start Date");
  return m;
};

// ─── Priority Badge ───────────────────────────────────────────────────────────
const PriorityBadge: React.FC<{ priority: string }> = ({ priority }) => {
  const map: Record<string,string> = {
    "Routine":"bg-blue-50 text-blue-600 border-blue-200",
    "Urgent":"bg-red-50 text-red-600 border-red-200",
    "STAT":"bg-orange-50 text-orange-600 border-orange-200",
    "As Soon As Possible":"bg-yellow-50 text-yellow-700 border-yellow-200",
  };
  return <span className={`inline-block border rounded px-2 py-0.5 text-[10px] font-semibold ${map[priority]??"bg-gray-50 text-gray-500 border-gray-200"}`}>{priority}</span>;
};

// ─── Validation Error ─────────────────────────────────────────────────────────
const ValidationError: React.FC<{ fields: string[] }> = ({ fields }) =>
  fields.length === 0 ? null : (
    <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-600">
      <span className="font-semibold">⚠ Please fill:</span><span>{fields.join(", ")}</span>
    </div>
  );

const NotesBox: React.FC<{
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  rows?: number;
}> = ({ value, onChange, placeholder = "Enter notes...", rows = 4 }) => {

  const { startListening } = useSpeechToText();

  return (
    <div className="relative border border-gray-300 rounded-md bg-white">
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        className="w-full px-3 py-2 outline-none resize-none text-xs text-gray-700"
      />

      <div className="absolute right-2 bottom-2 flex items-center gap-2">
        {/* 🎤 MIC */}
        <button
          title="Mic"
          onClick={() =>
            startListening((text) =>
              onChange(value ? value + " " + text : text)
            )
          }
        >
          <MdMic className="text-[#128273]" size={20} />
        </button>

        <button title="Save">
          <MdSave className="text-blue-500" size={20} />
        </button>
      </div>
    </div>
  );
};


// ─── Date Field ───────────────────────────────────────────────────────────────
const DateField: React.FC<{label:string; required?:boolean; value:string; onChange:(v:string)=>void}> =
  ({ label, required, value, onChange }) => (
  <div className="space-y-1.5">
    <label className="text-xs font-semibold text-gray-700">{label} {required&&<span className="text-red-500">*</span>}</label>
    <div className="relative w-[190px]">
      <input type="text" value={value} onChange={(e)=>onChange(e.target.value)} placeholder="DD/MM/YYYY"
        className="w-full border border-gray-300 rounded-md pl-3 pr-9 py-2 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#159BD7] text-gray-700"/>
      <label className="absolute right-2 top-1/2 -translate-y-1/2 cursor-pointer text-[#159BD7]">
        <FiCalendar size={15}/>
        <input type="date" className="absolute opacity-0 w-0 h-0 pointer-events-none"
          onChange={(e)=>{ if(e.target.value) onChange(new Date(e.target.value).toLocaleDateString("en-GB")); }}/>
      </label>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const OPDPrescriptionEMR: React.FC = () => {
  const { startListening } = useSpeechToText();
  const [openSection, setOpenSection] = useState<string|null>("planOfCare");
  const toggleSection = (key: string) => setOpenSection(p => p===key ? null : key);

  // ── Chief Complaint ────────────────────────────────────────────────────────
  const [complaintRows, setComplaintRows] = useState<ComplaintRow[]>([
    { problem:"Headache", severity:"Medium", bodyPart:"Head", duration:"2", unit:"Years" },
  ]);
  const [complaintErrors, setComplaintErrors] = useState<Record<number,string[]>>({});

  const updateComplaint = (i: number, field: keyof ComplaintRow, val: string) => {
    const rows = [...complaintRows]; rows[i]={...rows[i],[field]:val}; setComplaintRows(rows);
    if (complaintErrors[i]) setComplaintErrors(p=>{ const n={...p}; delete n[i]; return n; });
  };
  const handleAddComplaintRow = (idx: number) => {
    const missing = getMissingComplaintFields(complaintRows[idx]);
    if (missing.length>0) { setComplaintErrors(p=>({...p,[idx]:missing})); return; }
    setComplaintErrors(p=>{ const n={...p}; delete n[idx]; return n; });
    setComplaintRows([...complaintRows,{ problem:"", severity:"", bodyPart:"", duration:"", unit:"Years" }]);
  };

  const [history, setHistory]         = useState("Headaches began approximately 6 months ago. Typically on left side of the head.");
  const [examination, setExamination] = useState("");
  const [instruction, setInstruction] = useState("");

  const [planTab, setPlanTab]                            = useState<PlanTab>("medication");
  const [notTreatmentRequired, setNotTreatmentRequired] = useState(false);

  const [adviseInvYes, setAdviseInvYes]         = useState(true);
  const [serviceSearch, setServiceSearch]       = useState("");
  const [showServiceDrop, setShowServiceDrop]   = useState(false);
  const [pendingPriority, setPendingPriority]   = useState("Routine");
  const [pendingService, setPendingService]     = useState<typeof DUMMY_SERVICES[0]|null>(null);
  const [invAddError, setInvAddError]           = useState("");
  const [examValues, setExamValues] = useState<Record<string, string>>({});
const updateExamField = (field: string, val: string) =>
  setExamValues(prev => ({ ...prev, [field]: val }));
  const [selectedServices, setSelectedServices] = useState<ServiceRow[]>([
    { id:6, serviceName:"Complete Blood Count (CBC)", department:"Pathology", subDepartment:"Hematology", priority:"Routine" },
  ]);
  const filteredServices = DUMMY_SERVICES.filter(s=>
    s.serviceName.toLowerCase().includes(serviceSearch.toLowerCase())||
    s.department.toLowerCase().includes(serviceSearch.toLowerCase())||
    s.subDepartment.toLowerCase().includes(serviceSearch.toLowerCase())
  );
  const addPendingService = () => {
    if (!pendingService) { setInvAddError("Please search and select an investigation."); return; }
    if (selectedServices.find(x=>x.id===pendingService.id)) { setInvAddError(`"${pendingService.serviceName}" is already added.`); return; }
    setSelectedServices(p=>[...p,{ id:pendingService.id, serviceName:pendingService.serviceName, department:pendingService.department, subDepartment:pendingService.subDepartment, priority:pendingPriority }]);
    setPendingService(null); setServiceSearch(""); setPendingPriority("Routine"); setInvAddError("");
  };
  const updateServicePriority = (i: number, val: string) => {
    const r=[...selectedServices]; r[i]={...r[i],priority:val}; setSelectedServices(r);
  };

  // ── ✅ Advised Medication — Single Input Row Design ───────────────────────
  const [adviseMedYes, setAdviseMedYes]     = useState(true);
  const [medType, setMedType]               = useState<"brand"|"generic">("brand");
  const [medSearch, setMedSearch]           = useState("");
  const [showMedDrop, setShowMedDrop]       = useState(false);
  const [medFormErrors, setMedFormErrors]   = useState<string[]>([]);  // single form errors
  const [editingMedIdx, setEditingMedIdx]   = useState<number|null>(null); // null = add mode, number = edit mode

  // The ONE input row state
  const [medForm, setMedForm] = useState<MedicineRow>(EMPTY_MED_FORM());

  // Saved/added medicines list
  const [savedMedicines, setSavedMedicines] = useState<MedicineRow[]>([{
    medicine:"TAB D RISE-60K (1+1)", dose:"OD", frequency:"0-0-0-1",
    morning:"0", afternoon:"0", evening:"0", night:"1",
    days:"3", startDate:"13/09/2015", route:"Oral", mealRelation:"After meal", instructions:"",
  }]);

  const filteredMeds = DUMMY_MEDICINES.filter(m=>m.toLowerCase().includes(medSearch.toLowerCase()));
const apiResponse = {
  templates: [
        {
            "TemplateId": 6,
            "TemplateCode": "ORTHO_EXAM_V1",
            "TemplateName": "Orthopaedics — Examination Template",
            "TemplateVersion": 1,
            "TemplateCategory": "Examination",
            "IsDefault": true,
            "TemplateJson": "{\n  \"templateCode\": \"ORTHO_EXAM_V1\",\n  \"specialization\": \"Orthopaedics\",\n  \"category\": \"Examination\",\n  \"sections\": [\n    {\n      \"sectionCode\": \"GPE\",\n      \"sectionName\": \"General Physical Examination\",\n      \"type\": \"single\",\n      \"fields\": [\n        {\"fieldCode\":\"GPE_Gait\",       \"label\":\"Gait\",          \"dataType\":\"SingleSelect\",\"options\":[\"Normal\",\"Antalgic\",\"Trendelenburg\",\"Waddling\",\"Steppage\",\"Short Leg\"]},\n        {\"fieldCode\":\"GPE_Posture\",    \"label\":\"Posture \/ Spine\",\"dataType\":\"SingleSelect\",\"options\":[\"Normal\",\"Kyphosis\",\"Lordosis\",\"Scoliosis\",\"List\"]},\n        {\"fieldCode\":\"GPE_Pallor\",     \"label\":\"Pallor\",         \"dataType\":\"SingleSelect\",\"options\":[\"Absent\",\"Present\"]},\n        {\"fieldCode\":\"GPE_Edema\",      \"label\":\"Edema\",          \"dataType\":\"SingleSelect\",\"options\":[\"Absent\",\"Present\"]},\n        {\"fieldCode\":\"GPE_Conscious\",  \"label\":\"Consciousness\",  \"dataType\":\"SingleSelect\",\"options\":[\"Alert\",\"Drowsy\"]}\n      ]\n    },\n    {\n      \"sectionCode\": \"LOCAL_EXAM\",\n      \"sectionName\": \"Local Examination\",\n      \"type\": \"single\",\n      \"fields\": [\n        {\"fieldCode\":\"LE_Joint\",      \"label\":\"Joint Examined\",   \"dataType\":\"Text\"},\n        {\"fieldCode\":\"LE_Side\",       \"label\":\"Side\",             \"dataType\":\"SingleSelect\",\"options\":[\"Left\",\"Right\",\"Bilateral\"]},\n        {\"fieldCode\":\"LE_Swelling\",   \"label\":\"Swelling\",         \"dataType\":\"SingleSelect\",\"options\":[\"Absent\",\"Present\"]},\n        {\"fieldCode\":\"LE_Deformity\",  \"label\":\"Deformity\",        \"dataType\":\"Text\"},\n        {\"fieldCode\":\"LE_Warmth\",     \"label\":\"Local Warmth\",     \"dataType\":\"SingleSelect\",\"options\":[\"Absent\",\"Present\"]},\n        {\"fieldCode\":\"LE_Tenderness\", \"label\":\"Tenderness\",       \"dataType\":\"SingleSelect\",\"options\":[\"Absent\",\"Grade I\",\"Grade II\",\"Grade III\",\"Grade IV\"]},\n        {\"fieldCode\":\"LE_TenderSite\", \"label\":\"Tenderness Site\",  \"dataType\":\"Text\"},\n        {\"fieldCode\":\"LE_Effusion\",   \"label\":\"Joint Effusion\",   \"dataType\":\"SingleSelect\",\"options\":[\"Absent\",\"Mild\",\"Moderate\",\"Tense\"]},\n        {\"fieldCode\":\"LE_Crepitus\",   \"label\":\"Crepitus\",         \"dataType\":\"SingleSelect\",\"options\":[\"Absent\",\"Present\"]}\n      ]\n    },\n    {\n      \"sectionCode\": \"ROM\",\n      \"sectionName\": \"Range of Motion\",\n      \"type\": \"single\",\n      \"fields\": [\n        {\"fieldCode\":\"ROM_Flexion\",       \"label\":\"Flexion (degrees)\",      \"dataType\":\"Number\"},\n        {\"fieldCode\":\"ROM_Extension\",     \"label\":\"Extension (degrees)\",    \"dataType\":\"Number\"},\n        {\"fieldCode\":\"ROM_Abduction\",     \"label\":\"Abduction (degrees)\",   \"dataType\":\"Number\"},\n        {\"fieldCode\":\"ROM_Adduction\",     \"label\":\"Adduction (degrees)\",   \"dataType\":\"Number\"},\n        {\"fieldCode\":\"ROM_IntRotation\",   \"label\":\"Int. Rotation (degrees)\",\"dataType\":\"Number\"},\n        {\"fieldCode\":\"ROM_ExtRotation\",   \"label\":\"Ext. Rotation (degrees)\",\"dataType\":\"Number\"},\n        {\"fieldCode\":\"ROM_Limitation\",    \"label\":\"Limitation Notes\",       \"dataType\":\"LongText\"}\n      ]\n    },\n    {\n      \"sectionCode\": \"SPECIAL_TESTS\",\n      \"sectionName\": \"Special Tests\",\n      \"type\": \"single\",\n      \"fields\": [\n        {\"fieldCode\":\"ST_SLR\",       \"label\":\"SLR (Straight Leg Raise)\",\"dataType\":\"SingleSelect\",\"options\":[\"Negative\",\"Positive Left\",\"Positive Right\",\"Positive Bilateral\"]},\n        {\"fieldCode\":\"ST_FABER\",     \"label\":\"FABER Test\",              \"dataType\":\"SingleSelect\",\"options\":[\"Negative\",\"Positive\"]},\n        {\"fieldCode\":\"ST_Lachman\",   \"label\":\"Lachman Test\",            \"dataType\":\"SingleSelect\",\"options\":[\"Negative\",\"Positive\",\"N\/A\"]},\n        {\"fieldCode\":\"ST_McMurray\",  \"label\":\"McMurray Test\",           \"dataType\":\"SingleSelect\",\"options\":[\"Negative\",\"Positive Medial\",\"Positive Lateral\",\"N\/A\"]},\n        {\"fieldCode\":\"ST_Drawer\",    \"label\":\"Drawer Test\",             \"dataType\":\"SingleSelect\",\"options\":[\"Negative\",\"Anterior Positive\",\"Posterior Positive\",\"N\/A\"]},\n        {\"fieldCode\":\"ST_Thompson\",  \"label\":\"Thompson Test\",           \"dataType\":\"SingleSelect\",\"options\":[\"Negative\",\"Positive\",\"N\/A\"]},\n        {\"fieldCode\":\"ST_Narrative\", \"label\":\"Additional Tests\",        \"dataType\":\"LongText\"}\n      ]\n    },\n    {\n      \"sectionCode\": \"NEURO_LIMB\",\n      \"sectionName\": \"Neurovascular Status\",\n      \"type\": \"single\",\n      \"fields\": [\n        {\"fieldCode\":\"NV_Sensation\",  \"label\":\"Sensation\",          \"dataType\":\"SingleSelect\",\"options\":[\"Intact\",\"Diminished\",\"Absent\"]},\n        {\"fieldCode\":\"NV_MotorPower\", \"label\":\"Motor Power\",        \"dataType\":\"SingleSelect\",\"options\":[\"0\/5\",\"1\/5\",\"2\/5\",\"3\/5\",\"4\/5\",\"5\/5\"]},\n        {\"fieldCode\":\"NV_Pulses\",     \"label\":\"Distal Pulses\",      \"dataType\":\"SingleSelect\",\"options\":[\"Present\",\"Weak\",\"Absent\"]},\n        {\"fieldCode\":\"NV_Capillary\",  \"label\":\"Capillary Refill\",   \"dataType\":\"SingleSelect\",\"options\":[\"Normal (<2s)\",\"Delayed (>2s)\",\"Absent\"]}\n      ]\n    },\n    {\n      \"sectionCode\": \"ORTHO_SCORES\",\n      \"sectionName\": \"Scoring Systems\",\n      \"type\": \"single\",\n      \"fields\": [\n        {\"fieldCode\":\"OS_VAS\",      \"label\":\"VAS Pain Score\",    \"dataType\":\"Number\",\"min\":0,\"max\":10},\n        {\"fieldCode\":\"OS_KSS\",      \"label\":\"Knee Society Score\",\"dataType\":\"Number\",\"min\":0,\"max\":200},\n        {\"fieldCode\":\"OS_HHS\",      \"label\":\"Harris Hip Score\",  \"dataType\":\"Number\",\"min\":0,\"max\":100},\n        {\"fieldCode\":\"OS_DASH\",     \"label\":\"DASH Score\",        \"dataType\":\"Number\",\"min\":0,\"max\":100},\n        {\"fieldCode\":\"OS_ODI\",      \"label\":\"ODI (Spine)\",       \"dataType\":\"Number\",\"min\":0,\"max\":100}\n      ]\n    }\n  ]\n}"
        }
    ]
};
  const updateMedForm = (field: keyof MedicineRow, val: string) => {
    setMedForm(p=>({...p,[field]:val}));
    if (medFormErrors.length>0) setMedFormErrors([]);
  };

  // ✅ Add OR Update based on editingMedIdx
  const handleMedFormSubmit = () => {
    const missing = getMissingMedFields(medForm);
    if (missing.length>0) { setMedFormErrors(missing); return; }
    setMedFormErrors([]);
    if (editingMedIdx !== null) {
      // Update existing
      const updated = [...savedMedicines];
      updated[editingMedIdx] = { ...medForm };
      setSavedMedicines(updated);
      setEditingMedIdx(null);
    } else {
      // Add new
      setSavedMedicines(p=>[...p, { ...medForm }]);
    }
    setMedForm(EMPTY_MED_FORM());
    setMedSearch("");
  };

  // ✅ Load a saved medicine into the input form for editing
  const handleEditMed = (i: number) => {
    setMedForm({ ...savedMedicines[i] });
    setMedSearch(savedMedicines[i].medicine);
    setEditingMedIdx(i);
    setMedFormErrors([]);
  };

  // Cancel edit mode
  const handleCancelEdit = () => {
    setMedForm(EMPTY_MED_FORM());
    setMedSearch("");
    setEditingMedIdx(null);
    setMedFormErrors([]);
  };

  const removeSavedMedicine = (i: number) => {
    setSavedMedicines(p=>p.filter((_,idx)=>idx!==i));
    if (editingMedIdx===i) handleCancelEdit();
  };

  // Shared field error check for the single form
  const isFErr = (field: string) => medFormErrors.some(f=>f.toLowerCase().replace(" ","")===field.toLowerCase().replace(" ",""));

  // ── Advised Surgery ───────────────────────────────────────────────────────
  const [adviseSurgeryYes, setAdviseSurgeryYes] = useState(true);
  const [surgeryDate, setSurgeryDate]           = useState("13/09/2025");
  const [surgeryNotes, setSurgeryNotes]         = useState("");

  // ── Provisional Diagnosis ─────────────────────────────────────────────────
  const [diagSearch, setDiagSearch]       = useState("");
  const [showDiagDrop, setShowDiagDrop]   = useState(false);
  const [diagnosisRows, setDiagnosisRows] = useState<DiagnosisRow[]>([{
    icdCode:"3B21.Y",
    diagnosisName:"Haemorrhagic disorder due to other specified circulating anticoagulants or coagulation factors",
    stage:"N/A",
  }]);
  const filteredDiagnoses = DUMMY_DIAGNOSES.filter(d=>
    d.title.toLowerCase().includes(diagSearch.toLowerCase())||d.id.toLowerCase().includes(diagSearch.toLowerCase())
  );
  const addDiagnosis = (d: typeof DUMMY_DIAGNOSES[0]) => {
    if (diagnosisRows.find(r=>r.icdCode===d.id)) return;
    setDiagnosisRows([...diagnosisRows,{ icdCode:d.id, diagnosisName:d.title, stage:"N/A" }]);
    setDiagSearch(""); setShowDiagDrop(false);
  };
  const removeDiagnosis = (i: number) => setDiagnosisRows(diagnosisRows.filter((_,idx)=>idx!==i));

  // ── Advised Admission ─────────────────────────────────────────────────────
  const [adviseAdmissionYes, setAdviseAdmissionYes] = useState(true);
  const [advisedDate, setAdvisedDate]               = useState("13/09/2025");
  const [admissionNotes, setAdmissionNotes]         = useState("");

  // ── Follow-Up ─────────────────────────────────────────────────────────────
  const [followUpDate, setFollowUpDate]   = useState("17/09/2025");
  const [notApplicable, setNotApplicable] = useState(false);

  // ─── Styles ───────────────────────────────────────────────────────────────
  const sectionWrapCls   = "border border-[#A8D8EA] rounded-md bg-white mb-2 overflow-hidden";
  const sectionHeaderCls = "w-full flex justify-between items-center px-4 py-3 bg-[#EAF6FB] text-[#1A3A5C] font-semibold text-sm focus:outline-none";
  const inputCls         = "border border-gray-300 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#159BD7]";
  const inputErrCls      = "border border-red-400 rounded px-2 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-red-400";
  const selectCls        = "border border-gray-300 rounded px-1 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#159BD7]";
  const selectErrCls     = "border border-red-400 rounded px-1 py-1 text-xs bg-white focus:outline-none focus:ring-1 focus:ring-red-400";
  const tabActiveCls     = "bg-[#0F766E] text-white px-3 py-1.5 rounded text-xs font-medium";
  const tabInactiveCls   = "border border-gray-300 text-gray-600 px-3 py-1.5 rounded text-xs font-medium hover:bg-gray-50";
  const radioLabelCls    = "flex items-center gap-1.5 cursor-pointer text-xs";
  const isComplaintErr   = (i:number,f:string)=>(complaintErrors[i]??[]).some(x=>x.toLowerCase().replace(" ","")=== f.toLowerCase().replace(" ",""));

  return (
    <div className="min-h-screen p-3 font-sans">
      <div className="max-w-5xl mx-auto space-y-2">

        {/* ══ CHIEF COMPLAINT ══════════════════════════════════════════════════ */}
        <div className={sectionWrapCls}>
          <button className={sectionHeaderCls} onClick={()=>toggleSection("chiefComplaint")}>
            <span>Chief Complaint <span className="text-red-500">*</span></span>
            {openSection==="chiefComplaint"?<FiChevronUp className="w-4 h-4"/>:<FiChevronDown className="w-4 h-4"/>}
          </button>
          {openSection==="chiefComplaint" && (
            <div className="p-3 space-y-2">
              <div className="grid grid-cols-[2.5fr_1fr_1.2fr_80px_1.2fr_80px] gap-2 text-xs font-semibold text-gray-600 px-1">
                <span>Problem <span className="text-red-500">*</span></span>
                <span>Severity <span className="text-red-500">*</span></span>
                <span>Body Part <span className="text-red-500">*</span></span>
                <span>Duration <span className="text-red-500">*</span></span>
                <span>Unit</span><span/>
              </div>
              {complaintRows.map((row,i)=>(
                <div key={i} className="space-y-1">
                  <div className="grid grid-cols-[2.5fr_1fr_1.2fr_80px_1.2fr_80px] gap-2 items-center">
                    <input type="text" value={row.problem} onChange={e=>updateComplaint(i,"problem",e.target.value)} placeholder="Enter problem" className={`${isComplaintErr(i,"problem")?inputErrCls:inputCls} w-full`}/>
                    <select value={row.severity} onChange={e=>updateComplaint(i,"severity",e.target.value)} className={`${isComplaintErr(i,"severity")?selectErrCls:selectCls} w-full`}>
                      <option value="">Select *</option>
                      {SEVERITY_OPTIONS.map(s=><option key={s}>{s}</option>)}
                    </select>
                    <input type="text" value={row.bodyPart} onChange={e=>updateComplaint(i,"bodyPart",e.target.value)} placeholder="Body Part" className={`${isComplaintErr(i,"bodypart")?inputErrCls:inputCls} w-full`}/>
                    <input type="number" value={row.duration} onChange={e=>updateComplaint(i,"duration",e.target.value)} placeholder="0" className={`${isComplaintErr(i,"duration")?inputErrCls:inputCls} w-full`}/>
                    <select value={row.unit} onChange={e=>updateComplaint(i,"unit",e.target.value)} className={`${selectCls} w-full`}>
                      {UNIT_OPTIONS.map(u=><option key={u}>{u}</option>)}
                    </select>
                    <div className="flex items-center gap-1.5">
                      <button onClick={()=>handleAddComplaintRow(i)} title="Add new row"><BsPlusCircleFill className="text-green-600 hover:text-green-800" size={16}/></button>
                      <button onClick={()=>{setComplaintRows(complaintRows.filter((_,idx)=>idx!==i));setComplaintErrors(p=>{const n={...p};delete n[i];return n;});}} title="Delete"><BsTrashFill className="text-red-400 hover:text-red-600" size={14}/></button>
                      <button title="Save"><MdSave className="text-blue-500 hover:text-blue-700" size={16}/></button>
                    </div>
                  </div>
                  {complaintErrors[i]?.length>0 && <ValidationError fields={complaintErrors[i]}/>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ══ HISTORY ══════════════════════════════════════════════════════════ */}
        <div className={sectionWrapCls}>
          <button className={sectionHeaderCls} onClick={()=>toggleSection("history")}>
            <span>History <span className="text-red-500">*</span></span>
            {openSection==="history"?<FiChevronUp className="w-4 h-4"/>:<FiChevronDown className="w-4 h-4"/>}
          </button>
          {openSection==="history"&&<div className="p-3"><NotesBox value={history} onChange={setHistory} placeholder="Enter patient history..." rows={3}/></div>}
        </div>

        {/* ══ EXAMINATION ══════════════════════════════════════════════════════ */}
        <div className={sectionWrapCls}>
          <button className={sectionHeaderCls} onClick={()=>toggleSection("examination")}>
            <span>Examination</span>
            {openSection==="examination"?<FiChevronUp className="w-4 h-4"/>:<FiChevronDown className="w-4 h-4"/>}
          </button>
          {openSection==="examination"&&<div className="p-3">
   
{openSection === "examination" && (
  <div className="p-3 space-y-4">

    {/* Specialization badge (read-only — from doctor profile) */}
    <div className="flex items-center gap-2">
      {/* <span className="text-xs font-semibold text-gray-600">Specialization:</span>
      <span className="px-2 py-0.5 bg-[#EAF6FB] text-[#1A3A5C] text-xs rounded font-medium border border-[#A8D8EA]">
        {specialization}
      </span> */}
    </div>

   
    {/* <ExaminationRenderer
      specialization="Orthopedics"
      values={examValues}
      onChange={updateExamField}
    /> */}

<DynamicExamination
  apiResponse={apiResponse}
  initialValues={{}}
  onChange={(values) => console.log("Live changes:", values)}
  onApply={(values) => console.log("Submitted:", values)}
  onClear={() => console.log("Cleared")}
  showFooter={true}
/>


    {/* Always-present free-text notes */}
    <div className="space-y-1.5">
      <label className="text-xs font-semibold text-gray-700">
        Additional Notes
      </label>
      <NotesBox
        value={examination}
        onChange={setExamination}
        placeholder="Enter additional examination findings..."
        rows={3}
      />
    </div>

  </div>
)}

          </div>}
        </div>

        {/* ══ PLAN OF CARE ═════════════════════════════════════════════════════ */}
        <div className={sectionWrapCls}>
          <button className={sectionHeaderCls} onClick={()=>toggleSection("planOfCare")}>
            <span>Plan Of Care <span className="text-red-500">*</span></span>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-xs font-normal text-gray-600" onClick={e=>e.stopPropagation()}>
                <input type="checkbox" checked={notTreatmentRequired} onChange={e=>setNotTreatmentRequired(e.target.checked)} className="w-3.5 h-3.5"/>
                Not Treatment Required
              </label>
              {openSection==="planOfCare"?<FiChevronUp className="w-4 h-4"/>:<FiChevronDown className="w-4 h-4"/>}
            </div>
          </button>

          {openSection==="planOfCare" && !notTreatmentRequired && (
            <div className="p-3 space-y-4">
              <div className="flex flex-wrap gap-2">
                {([["investigation","Advised Investigation"],["medication","Advised Medication"],["surgery","Advised Surgery"],["diagnosis","Provisional Diagnosis"],["admission","Advised Admission"]] as [PlanTab,string][]).map(([key,label])=>(
                  <button key={key} onClick={()=>setPlanTab(key)} className={planTab===key?tabActiveCls:tabInactiveCls}>{label}</button>
                ))}
              </div>

              {/* ══ ADVISED INVESTIGATION ══════════════════════════════════════ */}
              {planTab==="investigation" && (
                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-xs">
                    <span className="font-medium text-[#1A3A5C]">Advise Investigation:</span>
                    <label className={radioLabelCls}><input type="radio" checked={adviseInvYes} onChange={()=>setAdviseInvYes(true)} className="accent-green-600 w-3.5 h-3.5"/>Yes</label>
                    <label className={radioLabelCls}><input type="radio" checked={!adviseInvYes} onChange={()=>setAdviseInvYes(false)} className="accent-green-600 w-3.5 h-3.5"/>No</label>
                  </div>
                  {adviseInvYes && (
                    <>
                      <div className="flex items-end gap-2">
                        <div className="flex-1 space-y-1">
                          <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wide">Investigation <span className="text-red-500">*</span></label>
                          <div className={`flex items-center rounded-md bg-white px-2 h-9 ${invAddError&&!pendingService?"border border-red-400":"border border-gray-300"}`}>
                        <FiSearch className="text-[#128273] mr-1" size={20}/>
                            <input type="text" value={serviceSearch}
                              onChange={e=>{setServiceSearch(e.target.value);setShowServiceDrop(true);setPendingService(null);setInvAddError("");}}
                              onFocus={()=>setShowServiceDrop(true)}
                              onBlur={()=>setTimeout(()=>setShowServiceDrop(false),200)}
                              placeholder="Search investigation by name / department..."
                              className="w-full outline-none text-xs"/>
                            {serviceSearch&&<button onMouseDown={()=>{setServiceSearch("");setPendingService(null);setInvAddError("");}} className="text-gray-400 hover:text-gray-600 ml-1 text-xs">✕</button>}
                            <MdOutlineFilterList className="text-[#128273] ml-1" size={25}/>
                          </div>
                          {showServiceDrop&&(
                            <div className="relative">
                              <div className="absolute z-50 top-0 left-0 right-0 bg-white border border-gray-200 rounded-md max-h-52 overflow-y-auto shadow-lg">
                                {filteredServices.length>0?filteredServices.map(s=>{
                                  const added=!!selectedServices.find(x=>x.id===s.id);
                                  return(<div key={s.id} onMouseDown={()=>{setPendingService(s);setServiceSearch(s.serviceName);setShowServiceDrop(false);setInvAddError("");}}
                                    className={`flex items-center justify-between px-3 py-2 cursor-pointer text-xs group ${added?"bg-gray-50 cursor-not-allowed":"hover:bg-[#EAF6FB]"}`}>
                                    <span className={added?"text-gray-400":"text-gray-700 group-hover:text-[#159BD7]"}>{s.serviceName}{added&&<span className="ml-2 text-[10px] italic">(already added)</span>}</span>
                                    <span className="text-gray-400 text-[10px] ml-4 flex-shrink-0">{s.department} › {s.subDepartment}</span>
                                  </div>);
                                }):<div className="px-3 py-2 text-xs text-gray-400 italic">No investigations found</div>}
                              </div>
                            </div>
                          )}
                        </div>
                        <div className="space-y-1">
                          <select value={pendingPriority} onChange={e=>setPendingPriority(e.target.value)} className="border border-gray-300 rounded-md px-2 py-1.5 text-xs bg-white h-9 min-w-[140px] focus:outline-none focus:ring-1 focus:ring-[#159BD7]">
                            {PRIORITY_OPTIONS.map(p=><option key={p}>{p}</option>)}
                          </select>
                        </div>
                        <div className="space-y-1">
                          <label className="text-[10px] text-transparent select-none">Add</label>
                          <button onClick={addPendingService} title={pendingService?`Add "${pendingService.serviceName}"`:"Select an investigation first"}
                            className={`flex items-center gap-1.5 px-4 py-2 rounded-md text-xs font-semibold h-9 border transition-all ${pendingService?"bg-[#0F766E] hover:bg-teal-700 text-white border-teal-700 shadow-sm":"bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"}`}>
                            <FiPlusCircle size={14}/>Add
                          </button>
                        </div>
                      </div>
                      {invAddError&&<div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-md text-xs text-red-600"><span>⚠ {invAddError}</span></div>}
                      {pendingService&&(
                        <div className="flex items-center gap-2 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-md text-xs text-teal-700">
                          <span className="font-medium">{pendingService.serviceName}</span><span className="text-teal-400">·</span>
                          <span className="text-teal-500">{pendingService.department}</span><span className="text-teal-400">·</span>
                          <PriorityBadge priority={pendingPriority}/><span className="ml-auto text-teal-400 text-[10px]">Click Add → to confirm</span>
                        </div>
                      )}
                      <div className="border border-gray-200 rounded-md overflow-hidden">
                        <table className="w-full text-xs">
                          <thead className="bg-[#EAF6FB] text-gray-600">
                            <tr>
                              <th className="p-2 text-left font-semibold"><div className="flex items-center gap-1">Service Name<FiChevronDown size={11}/>                            <MdOutlineFilterList className="text-[#128273] ml-1" size={25}/>
</div></th>
                              <th className="p-2 text-left font-semibold"><div className="flex items-center gap-1">Department<FiChevronDown size={11}/></div></th>
                              <th className="p-2 text-left font-semibold">Sub-Department</th>
                              <th className="p-2 text-left font-semibold w-[150px]">Priority</th>
                              <th className="p-2 text-center font-semibold w-[60px]">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {selectedServices.length===0?(<tr><td colSpan={5} className="p-4 text-center text-xs text-gray-400 italic">No investigations added.</td></tr>)
                            :selectedServices.map((s,i)=>(
                              <tr key={s.id} className={`border-t border-gray-100 ${i%2===0?"bg-white":"bg-gray-50/40"}`}>
                                <td className="p-2 text-gray-700 font-medium">{s.serviceName}</td>
                                <td className="p-2 text-gray-600">{s.department}</td>
                                <td className="p-2 text-gray-500">{s.subDepartment}</td>
                                <td className="p-2">
                                  <select value={s.priority} onChange={e=>updateServicePriority(i,e.target.value)}
                                    className={`border rounded px-1.5 py-0.5 text-[11px] font-semibold focus:outline-none ${s.priority==="Urgent"?"bg-red-50 text-red-600 border-red-200":s.priority==="STAT"?"bg-orange-50 text-orange-600 border-orange-200":s.priority==="As Soon As Possible"?"bg-yellow-50 text-yellow-700 border-yellow-200":"bg-blue-50 text-blue-600 border-blue-200"}`}>
                                    {PRIORITY_OPTIONS.map(p=><option key={p}>{p}</option>)}
                                  </select>
                                </td>
                                <td className="p-2 text-center"><button onClick={()=>setSelectedServices(p=>p.filter((_,idx)=>idx!==i))} title="Remove"><BsTrashFill className="text-gray-400 hover:text-red-500 mx-auto" size={13}/></button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* ══ ✅ ADVISED MEDICATION — Single Input Row ══════════════════ */}
              {planTab==="medication" && (
                <div className="space-y-3">
                  <div className="flex items-center gap-4 text-xs">
                    <span className="font-medium text-[#1A3A5C]">Advised Medication:</span>
                    <label className={radioLabelCls}><input type="radio" checked={adviseMedYes} onChange={()=>setAdviseMedYes(true)} className="accent-green-600 w-3.5 h-3.5"/>Yes</label>
                    <label className={radioLabelCls}><input type="radio" checked={!adviseMedYes} onChange={()=>setAdviseMedYes(false)} className="accent-green-600 w-3.5 h-3.5"/>No</label>
                  </div>

                  {adviseMedYes && (
                    <>
                      <div className="flex items-center gap-4 text-xs">
                        <label className={radioLabelCls}><input type="radio" checked={medType==="brand"} onChange={()=>setMedType("brand")} className="accent-green-600 w-3.5 h-3.5"/>Brand Name</label>
                        <label className={radioLabelCls}><input type="radio" checked={medType==="generic"} onChange={()=>setMedType("generic")} className="accent-orange-500 w-3.5 h-3.5"/>Generic Name</label>
                      </div>

                      {/* ✅ Edit mode banner */}
                      {editingMedIdx!==null && (
                        <div className="flex items-center justify-between px-3 py-2 bg-amber-50 border border-amber-300 rounded-md text-xs text-amber-700">
                          <span>✏ Editing: <strong>{savedMedicines[editingMedIdx]?.medicine}</strong> — Make changes and click Update</span>
                          <button onClick={handleCancelEdit} className="text-amber-500 hover:text-amber-700 font-medium ml-4">✕ Cancel</button>
                        </div>
                      )}

                      {/* ✅ Medicine search bar */}
                      <div className="relative">
                        <div className="flex items-center border border-gray-300 rounded-md bg-white px-2">
                        <FiSearch className="text-[#128273] mr-1" size={20}/>
                          <input type="text" value={medSearch}
                            onChange={e=>{setMedSearch(e.target.value);setShowMedDrop(true);}}
                            onFocus={()=>setShowMedDrop(true)}
                            onBlur={()=>setTimeout(()=>setShowMedDrop(false),200)}
                            placeholder="Search & select medicine to fill input row..."
                            className="w-full py-1.5 outline-none text-xs"/>
                          {medSearch&&<button onMouseDown={()=>{setMedSearch("");updateMedForm("medicine","");}} className="text-gray-400 hover:text-gray-600 text-xs">✕</button>}
                                                     <MdOutlineFilterList className="text-[#128273] ml-1" size={25}/>

                        </div>
                        {showMedDrop&&filteredMeds.length>0&&(
                          <div className="absolute z-50 top-full left-0 right-0 bg-white border border-gray-200 rounded-b-md max-h-48 overflow-y-auto shadow-lg">
                            {filteredMeds.map((m,idx)=>(
                              <div key={idx} onMouseDown={()=>{updateMedForm("medicine",m);setMedSearch(m);setShowMedDrop(false);}}
                                className="px-3 py-2 hover:bg-[#EAF6FB] cursor-pointer text-xs">{m}</div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* ✅ Single input row table */}
                      <div className="overflow-x-auto border border-gray-200 rounded-md custom-scrollbar">
                        <table className="w-full text-[11px] border-collapse">
                          <thead>
                            <tr className="bg-[#EAF6FB] text-gray-600 font-semibold border-b border-gray-200">
                              <th className="text-left p-2 min-w-[140px]">Medicine <span className="text-red-500">*</span></th>
                              <th className="text-left p-2 min-w-[80px]">Dose <span className="text-red-500">*</span></th>
                              <th className="text-left p-2 min-w-[105px]">Frequency <span className="text-red-500">*</span></th>
                              <th className="text-left p-2 w-12">M</th>
                              <th className="text-left p-2 w-12">A</th>
                              <th className="text-left p-2 w-12">E</th>
                              <th className="text-left p-2 w-12">N</th>
                              <th className="text-left p-2 w-14">Days <span className="text-red-500">*</span></th>
                              <th className="text-left p-2 min-w-[92px]">Start Date <span className="text-red-500">*</span></th>
                              <th className="text-left p-2 min-w-[90px]">Route</th>
                              <th className="text-left p-2 min-w-[92px]">Meal</th>
                              <th className="text-center p-2 w-16">Action</th>
                            </tr>
                          </thead>
                          <tbody>
                            {/* ✅ THE ONE INPUT ROW — always present */}
                            <tr className={`border-b border-gray-200 ${editingMedIdx!==null?"bg-amber-50/50":""}`}>
                              {/* Medicine (read-only, filled from search) */}
                              <td className="p-1">
                                <div className="relative group">
                                  <input type="text" value={medForm.medicine} readOnly placeholder="Select above ↑"
                                    title={medForm.medicine}
                                    className={`${isFErr("medicine")?inputErrCls:inputCls} w-full max-w-[138px] truncate cursor-default`}/>
                                  {medForm.medicine&&<div className="hidden group-hover:block absolute z-50 left-0 top-full bg-gray-800 text-white text-[10px] px-2 py-1 rounded whitespace-nowrap">{medForm.medicine}</div>}
                                </div>
                              </td>
                              <td className="p-1">
                                <select value={medForm.dose} onChange={e=>updateMedForm("dose",e.target.value)} className={`${isFErr("dose")?selectErrCls:selectCls} w-[78px]`}>
                                  <option value="">Select</option>
                                  {DOSE_OPTIONS.map(d=><option key={d}>{d}</option>)}
                                </select>
                              </td>
                              <td className="p-1">
                                <select value={medForm.frequency} onChange={e=>updateMedForm("frequency",e.target.value)} className={`${isFErr("frequency")?selectErrCls:selectCls} w-[100px]`}>
                                  <option value="">Select</option>
                                  {FREQUENCY_OPTIONS.map(f=><option key={f}>{f}</option>)}
                                </select>
                              </td>
                              <td className="p-1"><input type="text" value={medForm.morning} onChange={e=>updateMedForm("morning",e.target.value)} className={`${inputCls} w-9 text-center`}/></td>
                              <td className="p-1"><input type="text" value={medForm.afternoon} onChange={e=>updateMedForm("afternoon",e.target.value)} className={`${inputCls} w-9 text-center`}/></td>
                              <td className="p-1"><input type="text" value={medForm.evening} onChange={e=>updateMedForm("evening",e.target.value)} className={`${inputCls} w-9 text-center`}/></td>
                              <td className="p-1"><input type="text" value={medForm.night} onChange={e=>updateMedForm("night",e.target.value)} className={`${inputCls} w-9 text-center`}/></td>
                              <td className="p-1"><input type="text" value={medForm.days} onChange={e=>updateMedForm("days",e.target.value)} className={`${isFErr("days")?inputErrCls:inputCls} w-12`}/></td>
                              <td className="p-1">
                                <div className="relative">
                                  <input type="text" value={medForm.startDate} onChange={e=>updateMedForm("startDate",e.target.value)} placeholder="DD/MM/YYYY" className={`${isFErr("startdate")?inputErrCls:inputCls} w-[88px] pr-6`}/>
                                  <label className="absolute right-1.5 top-1/2 -translate-y-1/2 cursor-pointer text-[#159BD7]">
                                    <FiCalendar size={11}/>
                                    <input type="date" className="absolute opacity-0 w-0 h-0 pointer-events-none" onChange={e=>{ if(e.target.value) updateMedForm("startDate",new Date(e.target.value).toLocaleDateString("en-GB")); }}/>
                                  </label>
                                </div>
                              </td>
                              <td className="p-1"><select value={medForm.route} onChange={e=>updateMedForm("route",e.target.value)} className={`${selectCls} w-[88px]`}>{ROUTE_OPTIONS.map(r=><option key={r}>{r}</option>)}</select></td>
                              <td className="p-1"><select value={medForm.mealRelation} onChange={e=>updateMedForm("mealRelation",e.target.value)} className={`${selectCls} w-[90px]`}>{MEAL_OPTIONS.map(m=><option key={m}>{m}</option>)}</select></td>
                              {/* ✅ + / Update button */}
                              <td className="p-1 text-center">
                                <button onClick={handleMedFormSubmit}
                                  title={editingMedIdx!==null?"Update medicine":"Add medicine to list"}
                                  className={`flex items-center justify-center gap-1 mx-auto px-2 py-1 rounded text-xs font-semibold transition-all
                                    ${editingMedIdx!==null
                                      ?"bg-amber-500 hover:bg-amber-600 text-white"
                                      :"bg-[#0F766E] hover:bg-teal-700 text-white"}`}>
                                  {editingMedIdx!==null?<><FiCheck size={12}/>Update</>:<><BsPlusCircleFill size={12}/>Add</>}
                                </button>
                              </td>
                            </tr>
                            {/* Instruction row for the input form */}
                            <tr className="bg-gray-50 border-b border-gray-200">
                              <td colSpan={12} className="px-2 pb-2 pt-1">
                                <div className="relative border border-gray-200 rounded-md bg-white">
                                  <textarea value={medForm.instructions} onChange={e=>updateMedForm("instructions",e.target.value)}
                                    placeholder="Add Instruction for this medicine..." rows={2}
                                    className="w-full p-1.5 outline-none resize-none text-[10px] text-gray-600 pr-14"/>
                                  <div className="absolute right-2 bottom-2 flex gap-1.5">
                                    <MdMic className="text-[#128273] cursor-pointer" size={20}/>
                                    <MdSave className="text-blue-400 cursor-pointer" size={20}/>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>

                      {/* ✅ Validation error for the form */}
                      {medFormErrors.length>0 && <ValidationError fields={medFormErrors}/>}

                      {/* ✅ Added Medicines Summary Table */}
                      {savedMedicines.length>0 && (
                        <div className="border border-gray-200 rounded-md overflow-hidden">
                          <div className="bg-[#EAF6FB] px-3 py-2 flex items-center justify-between">
                            <span className="text-xs font-semibold text-[#1A3A5C]">Added Medicines ({savedMedicines.length})</span>
                          </div>
                          <table className="w-full text-xs">
                            <thead className="bg-gray-50 border-b border-gray-200 text-gray-500">
                              <tr>
                              
                                <th className="p-2 text-left font-semibold"><div className="flex items-center gap-1">Medicine Name<FiChevronDown size={10}/></div></th>
                                <th className="p-2 text-left font-semibold"><div className="flex items-center gap-1">Dosage <FiChevronDown size={10}/></div></th>
                                <th className="p-2 text-left font-semibold"><div className="flex items-center gap-1">Details<FiChevronDown size={10}/></div></th>
                                <th className="p-2 text-left font-semibold">Duration</th>
                               
                                <th className="p-2 text-center font-semibold w-[80px]">Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {savedMedicines.map((r,i)=>(
                                <tr key={i} className={`border-t border-gray-100 transition-colors ${editingMedIdx===i?"bg-amber-50 border-l-4 border-l-amber-400":i%2===0?"bg-white":"bg-gray-50/40"}`}>
                              
                                  <td className="p-2 font-medium text-gray-800 max-w-[160px]">
                                    <div className="truncate" title={r.medicine}>{r.medicine}</div>
                                   
                                  </td>
                               <td className="p-2 text-gray-600">
  <div className="grid grid-cols-1 text-start">
    <span className="font-mono">{r.frequency}</span>
    <span>M-A-E-N</span>
  </div>
</td>
                                  <td className="p-2 text-gray-600">
                                    {r.dose&&<span className="font-semibold uppercase text-[#0F766E]">{r.dose}</span>}
                                    {r.frequency&&<span className="text-gray-400"> |</span>}
                                    <br/>
                                    <span className="text-[10px] text-gray-400">{r.route}{r.mealRelation?` · ${r.mealRelation}`:""}</span>
                                  </td>
                                  <td className="p-2 text-gray-600">{r.days?`${r.days} days`:"—"}</td>
                              
                                  <td className="p-2">
                                    <div className="flex items-center justify-center gap-2">
                                      {/* ✅ Edit — loads into form */}
                                      <button onClick={()=>handleEditMed(i)} title="Edit this medicine"
                                        className={`transition-all ${editingMedIdx===i?"text-amber-500":"text-blue-400 hover:text-blue-600"}`}>
                                        <AiOutlineEdit size={15}/>
                                      </button>
                                      {/* Delete */}
                                      <button onClick={()=>removeSavedMedicine(i)} title="Remove" className="text-gray-400 hover:text-red-500 transition-colors">
                                        <BsTrashFill size={13}/>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ── Advised Surgery ───────────────────────────────────── */}
              {planTab==="surgery" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-medium text-[#1A3A5C]">Advised Surgery:</span>
                    <label className={radioLabelCls}><input type="radio" checked={adviseSurgeryYes} onChange={()=>setAdviseSurgeryYes(true)} className="accent-green-600 w-3.5 h-3.5"/>Yes</label>
                    <label className={radioLabelCls}><input type="radio" checked={!adviseSurgeryYes} onChange={()=>setAdviseSurgeryYes(false)} className="accent-green-600 w-3.5 h-3.5"/>No</label>
                  </div>
                  {adviseSurgeryYes&&(
                    <div className="space-y-3">
                      <DateField label="Surgery Date" required value={surgeryDate} onChange={setSurgeryDate}/>
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-700">Surgery Notes <span className="text-red-500">*</span></label>
                        <NotesBox value={surgeryNotes} onChange={setSurgeryNotes} placeholder="Surgery Notes" rows={4}/>
                      </div>
                    </div>
                  )}
                  {!adviseSurgeryYes&&<p className="text-xs text-gray-400 italic">No surgery advised for this patient.</p>}
                </div>
              )}

              {/* ── Provisional Diagnosis ─────────────────────────────── */}
              {planTab==="diagnosis" && (
                <div className="space-y-3">
                  <p className="text-xs font-semibold text-gray-700">Provisional Diagnosis Name :</p>
                  <div className="flex gap-2 items-center">
                    <div className="relative flex-1">
                      <div className="flex items-center border border-gray-300 rounded-md bg-white px-2">
                        <FiSearch className="text-[#128273] mr-1" size={20}/>
                        <input type="text" value={diagSearch}
                          onChange={e=>{setDiagSearch(e.target.value);setShowDiagDrop(true);}}
                          onFocus={()=>setShowDiagDrop(true)}
                          onBlur={()=>setTimeout(()=>setShowDiagDrop(false),200)}
                          placeholder="Search diagnosis by name / ICD code" className="w-full py-1.5 outline-none text-xs"/>
                                                  <MdOutlineFilterList className="text-[#128273] ml-1" size={25}/>

                      </div>
                      {showDiagDrop&&filteredDiagnoses.length>0&&(
                        <div className="absolute z-50 top-full left-0 right-0 bg-white border border-gray-200 rounded-b-md max-h-48 overflow-y-auto shadow-lg">
                          {filteredDiagnoses.map((d,idx)=>(
                            <div key={idx} onMouseDown={()=>addDiagnosis(d)} className="px-3 py-2 hover:bg-[#EAF6FB] cursor-pointer text-xs">
                              <span className="text-[#159BD7] font-medium">{d.id}</span> — {d.title}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center border border-gray-300 rounded-md bg-white px-3 py-1.5 text-xs text-gray-500 min-w-[200px] max-w-[300px] truncate">
                      {diagnosisRows.length>0?`${diagnosisRows[diagnosisRows.length-1].icdCode} — ${diagnosisRows[diagnosisRows.length-1].diagnosisName.substring(0,35)}...`:"Select ICD Code"}
                    </div>
                  </div>
                  {diagnosisRows.length>0&&(
                    <div className="border border-gray-200 rounded-md overflow-hidden">
                      <table className="w-full text-xs">
                        <thead className="bg-[#EAF6FB] text-gray-600">
                          <tr>
                            <th className="p-2 text-left font-semibold w-[100px]"><div className="flex items-center gap-1">ICD Code<FiChevronDown size={10}/></div></th>
                            <th className="p-2 text-left font-semibold"><div className="flex items-center gap-1">Diagnosis Name<FiChevronDown size={10}/></div></th>
                            <th className="p-2 text-left font-semibold w-[80px]">Stage</th>
                            <th className="p-2 text-center font-semibold w-[60px]">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {diagnosisRows.map((row,i)=>(
                            <tr key={i} className="border-t border-gray-100">
                              <td className="p-2 text-gray-700">{row.icdCode}</td>
                              <td className="p-2 text-gray-700">{row.diagnosisName}</td>
                              <td className="p-2 text-gray-400">{row.stage}</td>
                              <td className="p-2 text-center"><button onClick={()=>removeDiagnosis(i)}><BsTrashFill className="text-gray-400 hover:text-red-500 mx-auto" size={13}/></button></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              )}

              {/* ── Advised Admission ─────────────────────────────────── */}
              {planTab==="admission" && (
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <span className="text-xs font-medium text-[#1A3A5C]">Advised Admission:</span>
                    <label className={radioLabelCls}><input type="radio" checked={adviseAdmissionYes} onChange={()=>setAdviseAdmissionYes(true)} className="accent-green-600 w-3.5 h-3.5"/>Yes</label>
                    <label className={radioLabelCls}><input type="radio" checked={!adviseAdmissionYes} onChange={()=>setAdviseAdmissionYes(false)} className="accent-green-600 w-3.5 h-3.5"/>No</label>
                  </div>
                  {adviseAdmissionYes&&(
                    <div className="space-y-4">
                      <DateField label="Advised Date" required value={advisedDate} onChange={setAdvisedDate}/>
                      <div className="space-y-1.5">
                        <label className="text-xs font-semibold text-gray-700">Admission Notes <span className="text-red-500">*</span></label>
                        <NotesBox value={admissionNotes} onChange={setAdmissionNotes} placeholder="Admission Notes" rows={4}/>
                      </div>
                    </div>
                  )}
                  {!adviseAdmissionYes&&<p className="text-xs text-gray-400 italic">No admission advised for this patient.</p>}
                </div>
              )}
            </div>
          )}
          {openSection==="planOfCare"&&notTreatmentRequired&&<div className="p-4 text-xs text-gray-400 italic">Treatment marked as Not Required.</div>}
        </div>

        {/* ══ FOLLOW-UP ════════════════════════════════════════════════════════ */}
        <div className={sectionWrapCls}>
          <button className={sectionHeaderCls} onClick={()=>toggleSection("followUp")}>
            <span>Follow-Up <span className="text-red-500">*</span></span>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-1 text-xs font-normal text-gray-600" onClick={e=>e.stopPropagation()}>
                <input type="checkbox" checked={notApplicable} onChange={e=>setNotApplicable(e.target.checked)} className="w-3.5 h-3.5"/>
                Not Applicable
              </label>
              {openSection==="followUp"?<FiChevronUp className="w-4 h-4"/>:<FiChevronDown className="w-4 h-4"/>}
            </div>
          </button>
          {openSection==="followUp"&&!notApplicable&&<div className="p-3"><DateField label="Next Follow-Up Date" required value={followUpDate} onChange={setFollowUpDate}/></div>}
          {openSection==="followUp"&&notApplicable&&<div className="p-3 text-xs text-gray-400 italic">Follow-up marked as Not Applicable.</div>}
        </div>

        {/* ══ FOOTER ═══════════════════════════════════════════════════════════ */}
        <div className="flex items-center justify-end pt-2 pb-4">
          
          <div className="flex gap-2">
            <button className="px-4 py-1.5 rounded text-white text-sm font-medium bg-[#C8A82C] hover:bg-yellow-600">Save &amp; Draft</button>
            <button className="px-4 py-1.5 rounded text-white text-sm font-medium bg-[#00897B] hover:bg-teal-700">Provisional</button>
            <button className="px-4 py-1.5 rounded text-white text-sm font-medium bg-[#00695C] hover:bg-teal-900">Final</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OPDPrescriptionEMR;
